import {useMemo} from 'react'
import Chart from 'react-apexcharts'
import {useQuery} from 'react-query'
import {Statistics} from '../../../../@types/statistics'
import {UserAny} from '../../../../@types/user'
import {getStatisticsPlants} from '../../../../services/statiscsDateService'
import {Loading} from '../../../components/Loadings/loading'
import {KTSVG} from '../../../../../_metronic/helpers'
import {useThemeMode} from '../../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import {formatDate} from '../../../../utils/date'
import useSize from '../../../../hooks/useSize'

import styles from './adminDashboardPanel.module.css'

type Props = {
  userAny?: UserAny | null
}

type KpiCardProps = {
  icon: string
  iconClass: string
  iconBg: string
  value: string | number
  label: string
  hint?: string
}

const VISION_PRIMARY = '#ffc700'
const VISION_SECONDARY = '#f1bc00'
const VISION_DARK = '#181C32'

const PLAN_COLORS = [VISION_DARK, VISION_SECONDARY, VISION_PRIMARY]
const COMPANY_COLORS = [VISION_PRIMARY, VISION_DARK]
const REPORT_COLORS = [VISION_PRIMARY, VISION_SECONDARY]
const COMPARE_COLORS = [VISION_PRIMARY, VISION_DARK]

function formatCount(value: number | string | null | undefined) {
  const amount = Number(value) || 0
  if (amount >= 1000) {
    return amount.toLocaleString('pt-BR')
  }
  return String(amount)
}

function KpiCard({icon, iconClass, iconBg, value, label, hint}: KpiCardProps) {
  return (
    <div className={`card bg-dark ${styles.kpiCard}`}>
      <div className={`card-body ${styles.kpiBody}`}>
        <div className={styles.kpiTop}>
          <div className={`${styles.kpiIcon} ${iconBg}`}>
            <KTSVG path={icon} className={`svg-icon-2x ${iconClass}`} />
          </div>
        </div>
        <div className={`${styles.kpiValue} text-muted`}>{value}</div>
        <div className='text-light fw-semibold'>{label}</div>
        {hint && <div className='text-light fs-8 opacity-75'>{hint}</div>}
      </div>
    </div>
  )
}

export function AdminDashboardPanel({userAny}: Props) {
  const {width} = useSize()
  const {mode} = useThemeMode()

  const {data: statistics, isLoading} = useQuery<Statistics>(
    'getStatistics',
    async () => getStatisticsPlants()
  )

  const isDark = useMemo(() => {
    if (mode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return mode === 'dark'
  }, [mode])

  const chartTheme = useMemo(() => {
    const labelColor = isDark ? '#cdcdde' : '#5e6278'
    const gridColor = isDark ? '#2b2b40' : '#eff2f5'

    return {
      labelColor,
      gridColor,
      themeMode: (isDark ? 'dark' : 'light') as 'dark' | 'light',
    }
  }, [isDark])

  const planosImported = [
    statistics?.planosCompany?.basicaImported || 0,
    statistics?.planosCompany?.starterImported || 0,
    statistics?.planosCompany?.premiumImported || 0,
  ]
  const planosContratados = [
    statistics?.planosCompany?.basica || 0,
    statistics?.planosCompany?.starter || 0,
    statistics?.planosCompany?.premium || 0,
  ]
  const empresasSeries = [
    statistics?.companies?.ativa || 0,
    statistics?.companies?.desativada || 0,
  ]
  const totalUsinasImportadas = planosImported.reduce((acc, value) => acc + value, 0)
  const totalPlanosContratados = statistics?.planosCompany?.total || 0
  const totalEmpresas = statistics?.companies?.total || 0
  const relatoriosDetalhados = statistics?.report
  const relatoriosGeracao = statistics?.reportGeneration
  const mesReferencia = formatDate(
    new Date(relatoriosDetalhados?.referenceMonth || relatoriosGeracao?.referenceMonth || new Date())
  )

  const donutPlanosOptions: any = useMemo(
    () => ({
      chart: {type: 'donut', background: 'transparent', toolbar: {show: false}},
      theme: {mode: chartTheme.themeMode},
      labels: ['Básica', 'Starter', 'Premium'],
      colors: PLAN_COLORS,
      legend: {position: width < 1200 ? 'bottom' : 'right', labels: {colors: chartTheme.labelColor}},
      dataLabels: {
        enabled: true,
        formatter: (_val: number, opts: any) => formatCount(opts.w.config.series[opts.seriesIndex]),
      },
      tooltip: {
        y: {formatter: (val: number) => formatCount(val)},
      },
      stroke: {width: 0},
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              value: {
                formatter: (val: string) => formatCount(val),
              },
              total: {
                show: true,
                label: 'Usinas',
                color: chartTheme.labelColor,
                formatter: (w: any) =>
                  formatCount(w.globals.seriesTotals.reduce((acc: number, value: number) => acc + value, 0)),
              },
            },
          },
        },
      },
    }),
    [chartTheme, width]
  )

  const donutEmpresasOptions: any = useMemo(
    () => ({
      chart: {type: 'donut', background: 'transparent', toolbar: {show: false}},
      theme: {mode: chartTheme.themeMode},
      labels: ['Ativas', 'Desativadas'],
      colors: COMPANY_COLORS,
      legend: {position: width < 1200 ? 'bottom' : 'right', labels: {colors: chartTheme.labelColor}},
      dataLabels: {
        enabled: true,
        formatter: (_val: number, opts: any) => formatCount(opts.w.config.series[opts.seriesIndex]),
      },
      tooltip: {
        y: {formatter: (val: number) => formatCount(val)},
      },
      stroke: {width: 0},
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              value: {
                formatter: (val: string) => formatCount(val),
              },
              total: {
                show: true,
                label: 'Empresas',
                color: chartTheme.labelColor,
                formatter: (w: any) =>
                  formatCount(w.globals.seriesTotals.reduce((acc: number, value: number) => acc + value, 0)),
              },
            },
          },
        },
      },
    }),
    [chartTheme, width]
  )

  const barPlanosOptions: any = useMemo(
    () => ({
      chart: {
        type: 'bar',
        stacked: false,
        toolbar: {show: false},
        background: 'transparent',
      },
      theme: {mode: chartTheme.themeMode},
      plotOptions: {
        bar: {horizontal: false, borderRadius: 4, columnWidth: '45%'},
      },
      xaxis: {
        categories: ['Básica', 'Starter', 'Premium'],
        labels: {style: {colors: chartTheme.labelColor}},
        axisBorder: {show: false},
      },
      yaxis: {
        labels: {
          style: {colors: chartTheme.labelColor},
          formatter: (val: number) => formatCount(Math.round(val)),
        },
      },
      grid: {borderColor: chartTheme.gridColor, strokeDashArray: 4},
      legend: {position: 'top', labels: {colors: chartTheme.labelColor}},
      colors: COMPARE_COLORS,
      dataLabels: {enabled: false},
      tooltip: {
        y: {formatter: (val: number) => formatCount(val)},
      },
    }),
    [chartTheme]
  )

  const barRelatoriosOptions: any = useMemo(
    () => ({
      chart: {
        type: 'bar',
        stacked: true,
        toolbar: {show: false},
        background: 'transparent',
      },
      theme: {mode: chartTheme.themeMode},
      plotOptions: {
        bar: {horizontal: false, borderRadius: 4, columnWidth: '45%'},
      },
      xaxis: {
        categories: ['Detalhado', 'Geração'],
        labels: {style: {colors: chartTheme.labelColor}},
        axisBorder: {show: false},
      },
      yaxis: {
        labels: {
          style: {colors: chartTheme.labelColor},
          formatter: (val: number) => formatCount(Math.round(val)),
        },
      },
      grid: {borderColor: chartTheme.gridColor, strokeDashArray: 4},
      legend: {position: 'top', labels: {colors: chartTheme.labelColor}},
      colors: REPORT_COLORS,
      dataLabels: {enabled: false},
      tooltip: {
        y: {formatter: (val: number) => formatCount(val)},
      },
    }),
    [chartTheme]
  )

  if (isLoading) {
    return (
      <div className={styles.spinner}>
        <Loading myClass='spinner-border' />
      </div>
    )
  }

  return (
    <div>
      <div className={styles.header}>
        <h1 className='fw-bolder mb-1'>
          {userAny?.name ? `Olá, ${userAny.name}` : 'Olá, ADM'}
        </h1>
        <span className='text-muted fw-semibold'>
          Visão geral da plataforma — usinas, empresas, planos e relatórios
        </span>
      </div>

      <div className='row g-5 g-xl-8 mb-5 mb-xl-8'>
        <div className='col-6 col-xl-3'>
          <KpiCard
            icon='/media/icons/duotune/general/gen001.svg'
            iconClass='svg-icon-dark'
            iconBg='bg-light-dark'
            value={formatCount(statistics?.plantToday || 0)}
            label='Total de usinas'
            hint={`${formatCount(totalUsinasImportadas)} importadas nos planos`}
          />
        </div>
        <div className='col-6 col-xl-3'>
          <KpiCard
            icon='/media/icons/duotune/finance/fin001.svg'
            iconClass='svg-icon-dark'
            iconBg='bg-light-dark'
            value={formatCount(totalEmpresas)}
            label='Empresas'
            hint={`${formatCount(statistics?.companies?.ativa || 0)} ativas · ${formatCount(statistics?.companies?.desativada || 0)} desativadas`}
          />
        </div>
        <div className='col-6 col-xl-3'>
          <KpiCard
            icon='/media/icons/duotune/files/fil005.svg'
            iconClass='svg-icon-dark'
            iconBg='bg-light-dark'
            value={`${formatCount(relatoriosDetalhados?.gerado || 0)}/${formatCount(relatoriosDetalhados?.total || 0)}`}
            label='Relatórios detalhados'
            hint={`Mês ref.: ${mesReferencia}`}
          />
        </div>
        <div className='col-6 col-xl-3'>
          <KpiCard
            icon='/media/icons/duotune/graphs/gra007.svg'
            iconClass='svg-icon-dark'
            iconBg='bg-light-dark'
            value={`${formatCount(totalUsinasImportadas)} / ${formatCount(totalPlanosContratados)}`}
            label='Usinas nos planos'
            hint='Importadas / contratadas'
          />
        </div>
      </div>

      <div className='row g-5 g-xl-8 mb-5 mb-xl-8'>
        <div className='col-xl-6'>
          <div className={`card ${styles.chartCard}`}>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Usinas por plano</span>
                <span className='text-muted fw-bold fs-7'>Distribuição das usinas importadas</span>
              </h3>
            </div>
            <div className='card-body'>
              {totalUsinasImportadas > 0 ? (
                <Chart
                  options={donutPlanosOptions}
                  series={planosImported}
                  type='donut'
                  height={320}
                />
              ) : (
                <div className={styles.chartEmpty}>Nenhuma usina importada</div>
              )}
            </div>
          </div>
        </div>

        <div className='col-xl-6'>
          <div className={`card ${styles.chartCard}`}>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Empresas</span>
                <span className='text-muted fw-bold fs-7'>Status das empresas da plataforma</span>
              </h3>
            </div>
            <div className='card-body'>
              {totalEmpresas > 0 ? (
                <Chart
                  options={donutEmpresasOptions}
                  series={empresasSeries}
                  type='donut'
                  height={320}
                />
              ) : (
                <div className={styles.chartEmpty}>Nenhuma empresa cadastrada</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='row g-5 g-xl-8'>
        <div className='col-xl-6'>
          <div className={`card ${styles.chartCard}`}>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Planos contratados x importados</span>
                <span className='text-muted fw-bold fs-7'>Comparativo por tipo de plano</span>
              </h3>
            </div>
            <div className='card-body'>
              <Chart
                options={barPlanosOptions}
                series={[
                  {name: 'Contratado', data: planosContratados},
                  {name: 'Importado', data: planosImported},
                ]}
                type='bar'
                height={320}
              />
            </div>
          </div>
        </div>

        <div className='col-xl-6'>
          <div className={`card ${styles.chartCard}`}>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Relatórios do mês</span>
                <span className='text-muted fw-bold fs-7'>Referência: {mesReferencia}</span>
              </h3>
            </div>
            <div className='card-body'>
              <Chart
                options={barRelatoriosOptions}
                series={[
                  {
                    name: 'Gerado',
                    data: [relatoriosDetalhados?.gerado || 0, relatoriosGeracao?.gerado || 0],
                  },
                  {
                    name: 'Incompleto',
                    data: [relatoriosDetalhados?.incompleto || 0, relatoriosGeracao?.incompleto || 0],
                  },
                ]}
                type='bar'
                height={320}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
